import type { MergeExclusive } from 'type-fest';

import { useCallback } from 'react';

import { Slot } from '@radix-ui/react-slot';
import { useIntersectionObserver } from 'usehooks-ts';

import type { UnknownObject } from '@/types/utils';

import { type AppDispatch, type AppThunk, useAppDispatch } from '@/store';

import { trackViewOnScroll } from '../event/types/common';
import observer from '../observer';

export type TrackAppearanceProps<T> = React.ComponentProps<'div'> & {
  intersectionThreshold?: number;
  oncePerPage?: boolean;
} & MergeExclusive<
    {
      dataToTrack: T | T[];
      onAppearanceAction?: (dataToTrack: T[]) => AppThunk;
    },
    {
      dataToTrack?: undefined;
      onAppearanceAction?: () => AppThunk;
    }
  > &
  MergeExclusive<
    {
      asChild: true;
      children: React.ReactElement;
    },
    {
      asChild?: undefined;
      children?: React.ReactNode;
    }
  >;

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export const TrackAppearance = <T extends {} = never>({
  asChild,
  children,
  dataToTrack,
  intersectionThreshold,
  onAppearanceAction,
  oncePerPage,
  ...props
}: TrackAppearanceProps<T>) => {
  const dispatch = useAppDispatch();

  const trackEvent = useCallback(() => {
    if (!onAppearanceAction) return;

    if (dataToTrack == null) {
      dispatch((onAppearanceAction as () => AppThunk)());
      return;
    }

    const transformedToArray = Array.isArray(dataToTrack) ? dataToTrack : [dataToTrack];
    dispatch(onAppearanceAction(transformedToArray));
  }, [dataToTrack, dispatch, onAppearanceAction]);

  const [observe] = useIntersectionObserver({
    freezeOnceVisible: oncePerPage,
    onChange: (isVisible) => isVisible && trackEvent?.(),
    threshold: intersectionThreshold,
  });

  const Comp = asChild ? Slot : 'div';
  return (
    <Comp ref={observe} {...props}>
      {children}
    </Comp>
  );
};

export type TTrackingArg = {
  data: UnknownObject;
  key: string;
};

const onViewed = (trackingArgs: TTrackingArg[], dispatch: AppDispatch) => {
  const { data, key } = trackingArgs?.[0] ?? {};
  const names = observer.TRACKING_EVENTS_FOR_OBSERVER;
  const name = names[key as keyof typeof names];
  if (name) {
    observer.trackingEventsObserver.notify({ data: { ...data, dispatch }, name });
  }
};

export const trackOnScrollAction = trackViewOnScroll({ onViewed }) as (trackingArgs: TTrackingArg[]) => AppThunk;
